// Onglets QSE de l'écran UtileClean : frigos + historique des températures, historique des livraisons.
import { useState, useEffect, useCallback } from 'react'
import { Plus, Save, X, Thermometer, Truck, Image } from 'lucide-react'
import api from '../lib/api'
import { COLD_TYPES, PRODUCT_TYPES, frDate } from '../lib/qseRules'

interface ColdUnit {
  id: string; nom: string; type: string; tMax: number; tMin: number | null; relieEnregistreur: boolean
  sourceVoie: string | null; derniereValeur: number | null; derniereValeurAt: string | null; ordre: number; actif: boolean
}
interface Reading {
  id: string; date: string; moment: string; valeurEcran: number | null; valeurSonde: number | null; conforme: boolean
  degivrage?: boolean; actionCorrective: string | null; employeeName: string | null; coldUnit: { nom: string; tMax: number; tMin: number | null }
}
interface Reception {
  id: string; date: string; heure: string; fournisseur: string; typeProduits: string; tCamion: number | null; tColis: number | null
  dlcOk: boolean; emballageOk: boolean; camionPropre: boolean; conforme: boolean; decision: string; motif: string | null
  employeeName: string | null; hasPhoto: boolean
}

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'
const typeLabel = (t: string) => COLD_TYPES.find(x => x.value === t)?.label || t
const MOMENTS: Record<string, string> = { hebdo: 'Hebdo (sonde)', matin: 'Matin', soir: 'Soir' }
const DECISION_LABEL: Record<string, string> = { accepte: 'Acceptée', refuse: 'Refusée', reserve: 'Sous réserve' }

function UnitModal({ unit, establishmentId, onClose, onSaved }: { unit: Partial<ColdUnit>; establishmentId: string; onClose: () => void; onSaved: () => void }) {
  const isNew = !unit.id
  const [f, setF] = useState({
    nom: unit.nom || '', type: unit.type || 'positif',
    tMax: unit.tMax != null ? String(unit.tMax) : '', tMin: unit.tMin != null ? String(unit.tMin) : '',
    relieEnregistreur: unit.relieEnregistreur ?? false, sourceVoie: unit.sourceVoie || '', ordre: String(unit.ordre ?? 0), actif: unit.actif ?? true,
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  async function save() {
    setSaving(true); setError('')
    try {
      const body = { ...f, establishmentId, tMin: f.tMin === '' ? null : f.tMin }
      if (isNew) await api.post('/qse/cold-units', body)
      else await api.put(`/qse/cold-units/${unit.id}`, body)
      onSaved()
    } catch (e: any) { setError(e?.response?.data?.error || 'Enregistrement impossible') } finally { setSaving(false) }
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-bold text-gray-900">{isNew ? 'Nouveau frigo' : 'Modifier le frigo'}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className={lbl}>Nom (comme sur le boîtier)</label><input className={inp} value={f.nom} onChange={e => setF({ ...f, nom: e.target.value })} /></div>
          <div><label className={lbl}>Type</label>
            <select className={inp} value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
              {COLD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Max (°C)</label><input type="number" step="0.5" className={inp} value={f.tMax} onChange={e => setF({ ...f, tMax: e.target.value })} /></div>
            <div><label className={lbl}>Min (°C)</label><input type="number" step="0.5" className={inp} value={f.tMin} onChange={e => setF({ ...f, tMin: e.target.value })} placeholder="—" /></div>
            <div><label className={lbl}>Ordre</label><input type="number" className={inp} value={f.ordre} onChange={e => setF({ ...f, ordre: e.target.value })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.relieEnregistreur} onChange={e => setF({ ...f, relieEnregistreur: e.target.checked })} />
            Relié au boîtier d'alarme (sinon : relevé matin + soir au téléphone)
          </label>
          {f.relieEnregistreur && (
            <div><label className={lbl}>Voie sur le boîtier (facultatif)</label><input className={inp} value={f.sourceVoie} onChange={e => setF({ ...f, sourceVoie: e.target.value })} placeholder="ex. 3" /></div>
          )}
          {!isNew && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.actif} onChange={e => setF({ ...f, actif: e.target.checked })} /> En service
            </label>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end px-5 py-4 border-t">
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function TemperaturesPanel({ establishmentId }: { establishmentId: string }) {
  const [units, setUnits] = useState<ColdUnit[]>([])
  const [readings, setReadings] = useState<Reading[]>([])
  const [edit, setEdit] = useState<Partial<ColdUnit> | null>(null)
  const [onlyKo, setOnlyKo] = useState(false)
  const load = useCallback(() => {
    api.get(`/qse/cold-units?establishmentId=${establishmentId}`).then(r => setUnits(r.data)).catch(() => {})
    api.get(`/qse/temperatures?establishmentId=${establishmentId}`).then(r => setReadings(r.data)).catch(() => {})
  }, [establishmentId])
  useEffect(() => { load() }, [load])
  const shown = onlyKo ? readings.filter(r => !r.conforme) : readings

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Thermometer size={16} className="text-cyan-600" /> Frigos du magasin ({units.filter(u => u.actif).length})</h2>
          <button onClick={() => setEdit({})} className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-lg"><Plus size={14} /> Frigo</button>
        </div>
        {units.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">Aucun frigo. Créer la liste à partir des voies du boîtier d'alarme (même nom, mêmes seuils). Les tâches de relevé apparaissent toutes seules.</p>
        ) : (
          <div className="divide-y">
            {units.map(u => (
              <button key={u.id} onClick={() => setEdit(u)} className={`w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 ${u.actif ? '' : 'opacity-50'}`}>
                <div>
                  <div className="font-medium text-gray-900">{u.nom}{!u.actif && ' (hors service)'}</div>
                  <div className="text-xs text-gray-500">{typeLabel(u.type)} · {u.tMin != null ? `${u.tMin} à ${u.tMax}` : `max ${u.tMax}`} °C</div>
                </div>
                <div className="text-right">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${u.relieEnregistreur ? 'bg-cyan-100 text-cyan-800' : 'bg-amber-100 text-amber-800'}`}>
                    {u.relieEnregistreur ? 'Relié au boîtier' : 'Relevé matin + soir'}
                  </span>
                  {u.derniereValeur != null && u.derniereValeurAt && (
                    <div className="text-xs text-gray-500 mt-1">boîtier : {u.derniereValeur} °C à {new Date(u.derniereValeurAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Relevés des 30 derniers jours</h2>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={onlyKo} onChange={e => setOnlyKo(e.target.checked)} /> Hors limite seulement</label>
        </div>
        {shown.length === 0 ? <p className="p-6 text-sm text-gray-500 text-center">Aucun relevé.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                <th className="px-4 py-2">Date</th><th className="px-4 py-2">Moment</th><th className="px-4 py-2">Frigo</th>
                <th className="px-4 py-2">Écran</th><th className="px-4 py-2">Sonde</th><th className="px-4 py-2">Action corrective</th><th className="px-4 py-2">Par</th>
              </tr></thead>
              <tbody>
                {shown.map(r => (
                  <tr key={r.id} className={`border-b ${r.conforme ? '' : 'bg-red-50 text-red-900'}`}>
                    <td className="px-4 py-2 whitespace-nowrap">{frDate(r.date)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{MOMENTS[r.moment] || r.moment}{r.degivrage && <span className="ml-1 text-xs bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded">dégivrage</span>}</td>
                    <td className="px-4 py-2">{r.coldUnit?.nom}</td>
                    <td className="px-4 py-2">{r.valeurEcran ?? '—'}</td>
                    <td className="px-4 py-2 font-semibold">{r.valeurSonde ?? '—'}</td>
                    <td className="px-4 py-2">{r.conforme ? <span className="text-green-700">✓</span> : r.actionCorrective}</td>
                    <td className="px-4 py-2 text-gray-500">{r.employeeName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {edit && <UnitModal unit={edit} establishmentId={establishmentId} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load() }} />}
    </div>
  )
}

export function ReceptionsPanel({ establishmentId }: { establishmentId: string }) {
  const [rows, setRows] = useState<Reception[]>([])
  const [photo, setPhoto] = useState<string | null>(null)
  useEffect(() => {
    api.get(`/qse/receptions?establishmentId=${establishmentId}`).then(r => setRows(r.data)).catch(() => {})
  }, [establishmentId])
  async function showPhoto(id: string) {
    try { const r = await api.get(`/qse/receptions/${id}/photo`); setPhoto(r.data.photo) } catch { }
  }
  // Non conforme sans défaut visuel coché → c'est la température
  const visuel = (r: Reception) => {
    const v = [!r.dlcOk && 'dates', !r.emballageOk && 'emballages', !r.camionPropre && 'camion sale'].filter(Boolean)
    if (!r.conforme && v.length === 0) v.push('température')
    return v.join(', ')
  }

  return (
    <div className="bg-white rounded-xl border">
      <div className="px-5 py-4 border-b">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Truck size={16} className="text-orange-600" /> Livraisons des 30 derniers jours</h2>
        <p className="text-xs text-gray-500 mt-1">Saisies au téléphone par l'équipe (tâche « Contrôle des livraisons » ou bouton « + Livraison »).</p>
      </div>
      {rows.length === 0 ? <p className="p-6 text-sm text-gray-500 text-center">Aucune livraison contrôlée.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
              <th className="px-4 py-2">Date</th><th className="px-4 py-2">Fournisseur</th><th className="px-4 py-2">Produits</th>
              <th className="px-4 py-2">Camion</th><th className="px-4 py-2">Colis</th><th className="px-4 py-2">Problème</th>
              <th className="px-4 py-2">Décision</th><th className="px-4 py-2">Par</th><th className="px-4 py-2">BL</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={`border-b ${r.conforme ? '' : 'bg-red-50 text-red-900'}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{frDate(r.date)} {r.heure}</td>
                  <td className="px-4 py-2 font-medium">{r.fournisseur}</td>
                  <td className="px-4 py-2">{PRODUCT_TYPES.find(t => t.value === r.typeProduits)?.label.split(' (')[0] || r.typeProduits}</td>
                  <td className="px-4 py-2">{r.tCamion ?? '—'}</td>
                  <td className="px-4 py-2 font-semibold">{r.tColis ?? '—'}</td>
                  <td className="px-4 py-2">{visuel(r) || (r.conforme ? <span className="text-green-700">✓</span> : '')}</td>
                  <td className="px-4 py-2">
                    <span className={`font-semibold ${r.decision === 'accepte' ? 'text-green-700' : r.decision === 'refuse' ? 'text-red-700' : 'text-orange-600'}`}>{DECISION_LABEL[r.decision] || r.decision}</span>
                    {r.motif && <div className="text-xs">{r.motif}</div>}
                  </td>
                  <td className="px-4 py-2 text-gray-500">{r.employeeName || '—'}</td>
                  <td className="px-4 py-2">{r.hasPhoto && <button onClick={() => showPhoto(r.id)} className="text-blue-600"><Image size={16} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {photo && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPhoto(null)}>
          <img src={photo} alt="Bon de livraison" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  )
}
